import { useState, useEffect, useCallback, useRef } from 'react';
import type { TrailsData, TrailFilters, TagWithCount } from '@/types/trails';
import { trailsService } from '@/services/trails.service';
import { mockTrailsData } from '@/data/trailsMock';

/**
 * Hook para gerenciar dados das trilhas
 * Em desenvolvimento, usa dados mock
 * Em produção, busca do backend com filtros via API
 */
export const useTrails = () => {
  const [data, setData] = useState<TrailsData | null>(null);
  const [tags, setTags] = useState<TagWithCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TrailFilters>({
    search: '',
    tag: 'all',
  });
  const isDev = import.meta.env.DEV;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch tags on mount
  useEffect(() => {
    if (isDev) return;
    const fetchTags = async () => {
      try {
        const tagsData = await trailsService.getTags();
        setTags(tagsData);
      } catch {
        // Tags failing shouldn't block the page
        console.error('Erro ao carregar tags');
      }
    };
    fetchTags();
  }, [isDev]);

  // Fetch trails — re-fetches when filters change
  const fetchTrails = useCallback(
    async (currentFilters: TrailFilters) => {
      try {
        setIsLoading(true);

        if (isDev) {
          // Em desenvolvimento, usa dados mock (filtragem local)
          setTimeout(() => {
            let trails = [...mockTrailsData.trails];
            if (currentFilters.search) {
              const s = currentFilters.search.toLowerCase();
              trails = trails.filter(
                (t) =>
                  t.title.toLowerCase().includes(s) ||
                  t.description.toLowerCase().includes(s)
              );
            }
            setData({ ...mockTrailsData, trails });
            setIsLoading(false);
          }, 500);
        } else {
          const trailsData = await trailsService.getTrails({
            search: currentFilters.search || undefined,
            tag: currentFilters.tag !== 'all' ? currentFilters.tag : undefined,
          });
          setData(trailsData);
          setIsLoading(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar trilhas');
        setIsLoading(false);
      }
    },
    [isDev]
  );

  // Initial fetch
  useEffect(() => {
    fetchTrails(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const updateFilters = useCallback(
    (newFilters: Partial<TrailFilters>) => {
      setFilters((prev) => {
        const updated = { ...prev, ...newFilters };

        // Tag changes: fetch immediately
        if ('tag' in newFilters) {
          fetchTrails(updated);
        }

        // Search changes: debounce 400ms
        if ('search' in newFilters) {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            fetchTrails(updated);
          }, 400);
        }

        return updated;
      });
    },
    [fetchTrails]
  );

  return {
    data,
    tags,
    isLoading,
    error,
    filters,
    filteredTrails: data?.trails ?? [],
    updateFilters,
  };
};
